import React, { useState, useEffect, useLayoutEffect, useContext } from "react";
import { useSelector } from "react-redux";
import DateLib from "../../../inc/DateLib";
import ApiScript from "../../../app/api/admin/script/ApiScript";
import ApiDeviceScript from "../../../app/api/admin/device-script/ApiDeviceScript";
import ApiRedroidScript from "../../../app/api/admin/redroid-script/ApiRedroidScript";
import ApiNodeRedroidScript from "../../../app/api/admin/redroid-script/ApiNodeRedroidScript";
import ApiNode from "../../../app/api/admin/node/ApiNode";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Table from "../../modules/table";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import Button from "../../components/button";
import Select from "../../components/inputs/select";
import InputCheckbox from "../../components/inputs/checkbox";
import InputFile from "../../components/inputs/file";
import InputNumber from "../../components/inputs/number/number";
import InputText from "../../components/inputs/text";
import Loader from "../../components/loader";
import Modal from "../../components/modals/modal";
import "../../modules/list/__filters/styles.css";
import "./styles.css";

// Каталог скриптов — ТРИ вкладки. Раньше «Скрипты устройств» смешивала device_scripts (напрямую
// adb/ssh, App\Commands\Device\Scripts) и redroid_scripts is_device=true (через android-сервис,
// напр. reboot/reset) в одном списке — развели по разным вкладкам, чтобы отличать, что идёт
// напрямую, а что через android-сервис:
//   node            = node_scripts (SSH, App\Commands\Node\Scripts)
//                    + redroid_scripts is_node=true (через android-сервис, напр. prepare-farm)
//   device          = ТОЛЬКО device_scripts
//   device-redroid  = ТОЛЬКО redroid_scripts is_device=true
// «Выполнить» работает одинаково для обеих device-вкладок (kind='device' у обеих, см.
// DeviceScriptResource/RedroidScriptResource) — общий ScriptRunModal ветвится только на
// node/device, вкладок не касается. Скрытые (is_hidden) на бэке в index не отдаются. Лейбл
// правится ИНЛАЙН по клику на ячейку (без отдельной карточки show). У двух источников вкладки
// «Ноды» id — из РАЗНЫХ таблиц (могут совпадать числом) — при слиянии заводим синтетический
// уникальный row.id для key/локального стейта, настоящий id каталога — в row.catalogId (для
// колонки «ID» и PATCH-запроса).
export default function ScriptsIndex({ ...other }) {
    return <Page {...other}>
        <ScriptsIndexContent/>
    </Page>;
}

const TABS = [
    { key: 'node', label: 'Скрипты нод' },
    { key: 'device', label: 'Скрипты устройств' },
    { key: 'device-redroid', label: 'Redroid-скрипты' },
];

// [источник, Api-класс] на вкладку — источник нужен только для синтетического row.id.
const TAB_SOURCES = {
    node: [['node', ApiScript], ['node-redroid', ApiNodeRedroidScript]],
    device: [['device', ApiDeviceScript]],
    'device-redroid': [['device-redroid', ApiRedroidScript]],
};

const EMPTY_FILTER = { id: '', name: '' };
const EMPTY_STATE = { node: null, device: null, 'device-redroid': null };
const EMPTY_FILTERS_STATE = { node: { ...EMPTY_FILTER }, device: { ...EMPTY_FILTER }, 'device-redroid': { ...EMPTY_FILTER } };

function ScriptsIndexContent() {
    const contextContent = useContext(ContextContent);
    const canEdit = contextContent.checkPermission('edit');

    const [_tab, set_tab] = useState('device');
    const [_runScript, set_runScript] = useState(null);

    // Данные и фильтры — раздельно на вкладку (свой запрос, свой фильтр на каждую).
    const [_data, set_data] = useState({ ...EMPTY_STATE });
    const [_filters, set_filters] = useState({ ...EMPTY_FILTERS_STATE });
    const [_applied, set_applied] = useState({ ...EMPTY_FILTERS_STATE });

    useLayoutEffect(() => {
        contextContent.setTitle('Скрипты');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTab = async (tab) => {
        const results = await Promise.all(
            TAB_SOURCES[tab].map(async ([source, Api]) => {
                const data = await Api.Index().getResponse();
                return (data ?? []).map((row) => ({ ...row, catalogId: row.id, id: `${source}-${row.id}`, __api: Api }));
            }),
        );
        const merged = results.flat().sort((a, b) => a.name.localeCompare(b.name));
        set_data((prev) => ({ ...prev, [tab]: merged }));
    };

    // Ленивая загрузка вкладки при первом открытии.
    useEffect(() => {
        if (_data[_tab] === null) loadTab(_tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_tab]);

    const filter = _filters[_tab];
    const applied = _applied[_tab];

    const setFilter = (patch) => set_filters((prev) => ({ ...prev, [_tab]: { ...prev[_tab], ...patch } }));
    const applyFilters = () => set_applied((prev) => ({ ...prev, [_tab]: { ..._filters[_tab] } }));
    const resetFilters = () => {
        set_filters((prev) => ({ ...prev, [_tab]: { ...EMPTY_FILTER } }));
        set_applied((prev) => ({ ...prev, [_tab]: { ...EMPTY_FILTER } }));
    };

    // Локально обновляем лейбл строки после инлайн-сохранения (без перезагрузки списка).
    // Матчим по синтетическому row.id — он уникален в пределах вкладки, в отличие от catalogId.
    const applyLabel = (id, label) => {
        set_data((prev) => ({
            ...prev,
            [_tab]: (prev[_tab] ?? []).map((row) => (row.id === id ? { ...row, label } : row)),
        }));
    };

    const items = (_data[_tab] ?? []).filter((row) => {
        if (applied.id !== '' && String(row.catalogId) !== String(applied.id)) return false;
        if (applied.name !== '' && !row.name.toLowerCase().includes(applied.name.toLowerCase())) return false;
        return true;
    });

    const header = [
        <ListHeaderItem key="id" name="id" isSort={false}>ID</ListHeaderItem>,
        <ListHeaderItem key="name" name="name" isSort={false}>Имя</ListHeaderItem>,
        <ListHeaderItem key="label" name="label" isSort={false}>Лейбл</ListHeaderItem>,
        <ListHeaderItem key="created_at" name="created_at" isSort={false}>Создан</ListHeaderItem>,
        <ListHeaderItem key="run" name="run" isSort={false}>Выполнить</ListHeaderItem>,
    ];

    const renderRow = (row) => [
        <ListBodyItem key="id">{row.catalogId}</ListBodyItem>,
        <ListBodyItem key="name">{row.name}</ListBodyItem>,
        <ListBodyItem key="label">
            <LabelCell script={row} Api={row.__api} canEdit={canEdit} onSaved={(label) => applyLabel(row.id, label)}/>
        </ListBodyItem>,
        <ListBodyItem key="created_at">{DateLib.formatterDateTime(row.created_at) || '—'}</ListBodyItem>,
        <ListBodyItem key="run">
            {row.runnable && canEdit
                ? <Button className="btn-success" onClick={() => set_runScript(row)}>Выполнить</Button> : null}
        </ListBodyItem>,
    ];

    return <>
        <div className="scripts__tabs">
            {TABS.map((tab) => (
                <button
                    key={tab.key}
                    type="button"
                    className={`scripts__tab${_tab === tab.key ? ' scripts__tab_active' : ''}`}
                    onClick={() => set_tab(tab.key)}
                >
                    {tab.label}
                </button>
            ))}
        </div>

        <div className="list__filters">
            <div className="list__filters-inputs">
                <div className="list__filters-input">
                    <InputNumber className="input_wf" value={filter.id} onChange={(e) => setFilter({ id: e.target.value })}>id</InputNumber>
                </div>
                <div className="list__filters-input">
                    <InputText className="input_wf" value={filter.name} onChange={(e) => setFilter({ name: e.target.value })}>Имя</InputText>
                </div>
            </div>
            <div className="list__filters-actions">
                <Button className="btn-success" onClick={applyFilters}>Применить</Button>
                <Button onClick={resetFilters}>Сбросить</Button>
            </div>
        </div>

        {/* null — вкладку ещё не грузили (загрузка ленивая, при первом открытии). Без этого
            Table рисовал «Нет данных» — то есть врал, что каталог пуст. */}
        {_data[_tab] === null
            ? <Loader/>
            : <Table header={header} rows={items} renderRow={renderRow}/>}

        {_runScript ? (
            <ScriptRunModal script={_runScript} onClose={() => set_runScript(null)}/>
        ) : null}
    </>;
}

// Инлайн-редактирование лейбла: клик по ячейке → input, Enter/потеря фокуса — сохранить,
// Esc — отмена. Api — из row.__api (проставлен при слиянии источников вкладки, см. loadTab):
// строки одной вкладки могут прийти из РАЗНЫХ каталогов (напр. device_scripts + redroid_scripts).
function LabelCell({ script, Api, canEdit, onSaved }) {
    const [_editing, set_editing] = useState(false);
    const [_value, set_value] = useState(script.label || '');
    const [_saving, set_saving] = useState(false);

    useEffect(() => { set_value(script.label || ''); }, [script.label]);

    const save = async () => {
        if (_saving) return;
        if (_value === (script.label || '')) { set_editing(false); return; }
        set_saving(true);
        const data = await Api.Update(script.catalogId, { label: _value }).getResponse();
        set_saving(false);
        if (data) {
            onSaved(_value);
            set_editing(false);
        } else {
            set_value(script.label || '');
            set_editing(false);
        }
    };

    if (!canEdit) {
        return <span>{script.label || '—'}</span>;
    }

    if (!_editing) {
        return <span className="scripts__label" title="Изменить лейбл" onClick={() => set_editing(true)}>
            {script.label || <span className="scripts__label-empty">— задать —</span>}
        </span>;
    }

    return <input
        autoFocus
        className="input scripts__label-input"
        value={_value}
        disabled={_saving}
        onChange={(e) => set_value(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { set_value(script.label || ''); set_editing(false); }
        }}
    />;
}

// Пикер цели выполнения: нода (одна, Select) или устройства (несколько, чекбоксы) —
// в зависимости от script.kind. Запуск: node — синхронно (ApiNode.RunScript), device —
// через очередь (ApiDevice.BatchRunScript); install-apk — файл + batch/install.
function ScriptRunModal({ script, onClose }) {
    const nodes = useSelector((state) => state.option.nodes);

    const [_nodeId, set_nodeId] = useState('');
    const [_link, set_link] = useState(''); // launch-job: опциональная ссылка скрипта
    const [_apkFile, set_apkFile] = useState(null); // install-apk: файл для загрузки
    const [_devices, set_devices] = useState(null);
    const [_selectedDeviceIds, set_selectedDeviceIds] = useState([]);
    // «Сразу» — только для device-скриптов через очередь (install-apk — отдельный batch/install,
    // RunNow для него не заведён; node и так всегда синхронный). По умолчанию false — отложенно.
    const [_immediate, set_immediate] = useState(false);
    const [_isRunning, set_isRunning] = useState(false);
    const [_result, set_result] = useState(null);

    const isInstallApk = script.name === 'install-apk';
    const canToggleImmediate = script.kind === 'device' && !isInstallApk;

    useEffect(() => {
        if (script.kind !== 'device') return;
        (async () => {
            // per_page=200: без него Index() отдавал бы только первую страницу (20 устройств,
            // см. DevicesController::index) — пикер молча не давал выбрать ничего за пределами
            // первых 20, хотя чекбоксы выглядели как полный список.
            const api = ApiDevice.Index({ per_page: 200 });
            const data = await api.getResponse();
            set_devices(data ?? []);
        })();
    }, [script.kind]);

    const toggleDevice = (id, checked) => {
        set_selectedDeviceIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
    };

    const handleRun = async () => {
        set_isRunning(true);
        set_result(null);
        try {
            if (script.kind === 'node') {
                const extra = script.name === 'launch-job' && _link ? { link: _link } : {};
                const api = ApiNode.RunScript(_nodeId, script.name, extra);
                const data = await api.getResponse();
                set_result(data ?? { result: false });
            } else if (isInstallApk) {
                const data = await ApiDevice.BatchInstall(_selectedDeviceIds, _apkFile);
                set_result(data ?? { result: false });
            } else {
                const data = await ApiDevice.BatchRunScript(_selectedDeviceIds, script.name, { immediate: _immediate });
                set_result(data ?? { result: false });
            }
        } finally {
            set_isRunning(false);
        }
    };

    const canRun = script.kind === 'node'
        ? !!_nodeId
        : _selectedDeviceIds.length > 0 && (!isInstallApk || !!_apkFile);

    return <Modal isOpenModal={true} closeModal={onClose} title={`Выполнить: ${script.label || script.name}`}>
        {script.kind === 'node' ? (
            <Select className="input_wf" options={nodes} value={_nodeId} isNull={true}
                onChange={(e) => set_nodeId(e.target.value)}>Нода</Select>
        ) : (
            <div className="scripts__device-picker">
                {_devices === null
                    ? <Loader text="Загрузка устройств…"/>
                    : _devices.length === 0 ? 'Нет устройств' : null}
                {(_devices ?? []).map((device) => (
                    <InputCheckbox key={device.id} value={_selectedDeviceIds.includes(device.id)}
                        onChange={(e) => toggleDevice(device.id, e.target.checked)}>
                        {device.name} (#{device.id})
                    </InputCheckbox>
                ))}
            </div>
        )}

        {isInstallApk ? (
            <InputFile className="input_wf" value={_apkFile} accept=".apk"
                onChange={(e) => set_apkFile(e.target.files[0] ?? null)}>APK-файл</InputFile>
        ) : null}

        {script.name === 'launch-job' ? (
            <div className="scripts__trade-link">
                <label className="scripts__trade-link-label">Ссылка скрипта (пусто — из .env)</label>
                <input className="input_wf" type="text" value={_link} onChange={(e) => set_link(e.target.value)}/>
            </div>
        ) : null}

        {canToggleImmediate ? (
            <label className="scripts__run-immediate" title="Отменяет очередь на устройстве и стартует сразу, в обход крона">
                <InputCheckbox value={_immediate} onChange={(e) => set_immediate(e.target.checked)}/>
                Сразу (не отложенно)
            </label>
        ) : null}

        <div className="scripts__run-actions">
            <Button className="btn-success" disabled={!canRun || _isRunning} onClick={handleRun}>
                {_isRunning ? 'Запуск…' : 'Запустить'}
            </Button>
        </div>

        {_result ? (
            <div className="scripts__run-result">
                {isInstallApk
                    ? `Задача создана: установка на ${(_result.device_ids ?? []).length} устр. идёт в фоне`
                    : script.kind === 'device'
                        ? `Задача создана: запуск на ${(_result.dispatched_device_ids ?? []).length} устр. идёт в фоне`
                        : (_result.result ? 'Выполнено успешно' : `Ошибка: ${_result.error || 'неизвестно'}`)}
            </div>
        ) : null}

        {/* Устройства, на которые действие не встало: карта device_id => причина от бэкенда.
            Без неё разница между «выбрано 10» и «запуск на 7» ничем не объяснялась. Ключи объекта
            в JSON — строки, поэтому имя ищем через String(), иначе всегда получали бы «#51». */}
        {_result?.skipped_reasons && Object.keys(_result.skipped_reasons).length ? (
            <div className="scripts__run-skipped">
                {Object.entries(_result.skipped_reasons).map(([deviceId, reason]) => (
                    <div className="scripts__run-skipped-item" key={deviceId}>
                        {(_devices ?? []).find((item) => String(item.id) === deviceId)?.name ?? `#${deviceId}`}: {reason}
                    </div>
                ))}
            </div>
        ) : null}
    </Modal>;
}
